/**
 * `publicUrl` — the one way to address a file in `public/` from TypeScript.
 *
 * ## Why this exists
 *
 * The app is deployed twice over: the default build at
 * `https://a1.campfire3d.com/` and any number of named channels at
 * `https://a1.campfire3d.com/<channel>/` (see `scripts/deploy-client-dev.sh`).
 * A channel build is compiled with Vite's `base` set to `/<channel>/`, so every
 * asset it owns lives under that prefix — which is why a path must be resolved
 * against the running build rather than written as an absolute literal.
 *
 * Vite rewrites `base` into the places it can see: `index.html` attributes, CSS
 * `url(...)`, `import.meta.glob(..., { query: "?url" })`, and asset imports.
 * It does **not** rewrite plain string literals in TS — a `"/draco/"` handed to
 * `DRACOLoader.setDecoderPath` stays `"/draco/"` in the emitted bundle. Under a
 * channel that request leaves the channel and hits the origin root, where nginx
 * either serves the DEFAULT build's copy of the file or, for a path the root
 * build has no file for, answers the SPA fallback: `index.html` with a 200. The
 * second shape is the dangerous one — `response.ok` is true, so the caller gets
 * a parse error or an undecodable texture rather than a 404 it could report.
 *
 * That failure is worst for `public/AI/**`: prompts, roles and skills are
 * versioned with the build and are exactly what a channel exists to test, so a
 * channel would silently run new code against the default build's prompts and
 * read as a prompt regression.
 *
 * ## Contract
 *
 * `import.meta.env.BASE_URL` is Vite's normalized `base`. It always begins and
 * ends with `/` — `"/"` for the default build, `"/jay-testing/"` for a channel —
 * so callers pass a path with **no leading slash** and get back a root-absolute
 * URL for the running build.
 *
 * ```ts
 * publicUrl("draco/")                  // "/draco/"          | "/jay-testing/draco/"
 * publicUrl("materials/textures")      // "/materials/..."   | "/jay-testing/materials/..."
 * ```
 *
 * ## What must NOT go through here
 *
 * Same-origin **backend** paths are deliberately origin-rooted, because they
 * address nginx `location` blocks that live at the root of the shared server
 * block and are shared by every deployment on the host:
 *
 *   - `/__campfire_realtime` (`ai/orchestrator/AIRealtimeTransportModelRTC.ts`)
 *   - `/deployments.json`        (`models/deployments/DeploymentsModel.ts`)
 *
 * Prefixing any of those with the base would point them at a path nginx does not
 * proxy. External hosts (`cloudConfig.deeplinkBaseUri`, the AI service host) are
 * likewise untouched.
 */
export function publicUrl(path: string): string {
  // BASE_URL is guaranteed to end in "/", so a leading slash on `path` would
  // produce "//foo" — an absolute URL with an empty host on some parsers.
  return `${import.meta.env.BASE_URL}${path.replace(/^\/+/, "")}`;
}

/**
 * The channel this build was compiled for, or `""` for the default deployment.
 *
 * Derived from `base` rather than a separate `define` so there is exactly one
 * source of truth: a build whose assets resolve under `/jay-testing/` IS the
 * `jay-testing` deployment. Used by the dev panel to mark which row in the
 * deployments list is the one the reader is looking at.
 */
export function deploymentChannel(): string {
  return import.meta.env.BASE_URL.replace(/^\/+|\/+$/g, "");
}
